import {
    useCallback,
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    View
} from "react-native";
import type NotificationsListProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    TextInput,
    Button,
    Dialog,
    Text
} from "ncore-ui-kit";
import {
    type SentNotificationItem
} from "../../../../services/restAPI/actions/getAllSentNotifications/type";
import {
    Trash2Icon,
    PlusIcon,
    EyeIcon
} from "lucide-react-native";
import moment from "moment";
import RESTServiceContext from "../../../../services/restAPI/context";
import NotificationDetailModal from "../notificationDetailModal";
import Pagination from "../../../../components/pagination";

const NotificationsList = ({
    onOpenSendModal
}: NotificationsListProps) => {
    const {
        radiuses,
        borders,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        notifications,
        setNotifications
    ] = useState<SentNotificationItem[]>([]);

    const [
        totalCount,
        setTotalCount
    ] = useState<number>(0);

    const [
        page,
        setPage
    ] = useState<number>(1);

    const [
        searchQuery,
        setSearchQuery
    ] = useState<string>("");

    const [
        debouncedSearchQuery,
        setDebouncedSearchQuery
    ] = useState<string>("");

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    const [
        selectedNotificationId,
        setSelectedNotificationId
    ] = useState<string | null>(null);

    const [
        isDetailModalVisible,
        setIsDetailModalVisible
    ] = useState<boolean>(false);

    const [
        selectedDeleteId,
        setSelectedDeleteId
    ] = useState<string | null>(null);

    const [
        isDeleteDialogVisible,
        setIsDeleteDialogVisible
    ] = useState<boolean>(false);

    const [
        isDeleting,
        setIsDeleting
    ] = useState<boolean>(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500);

        return () => clearTimeout(timer);
    }, [
        searchQuery
    ]);

    const fetchNotifications = useCallback(async (currentPage: number, search: string) => {
        try {
            setLoading(true);
            const requestParams: {
                search?: string;
                limit: number;
                page: number;
            } = {
                page: currentPage,
                limit: 15
            };

            if(search.trim().length > 0) {
                requestParams.search = search.trim();
            }

            const response = await RESTServiceContext.action("getAllSentNotifications", requestParams);

            if(response?.payload) {
                setNotifications(response.payload.notifications || []);
                setTotalCount(response.payload.totalCount || 0);
            } else {
                setNotifications([]);
                setTotalCount(0);
            }
        } catch(error) {
            console.error(error);
            setNotifications([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchNotifications(page, debouncedSearchQuery);
    }, [
        debouncedSearchQuery,
        fetchNotifications,
        page
    ]);

    const handleOpenDetail = (id: string) => {
        setSelectedNotificationId(id);
        setIsDetailModalVisible(true);
    };

    const handleCloseDetail = () => {
        setSelectedNotificationId(null);
        setIsDetailModalVisible(false);
    };

    const handleOpenDeleteDialog = (id: string) => {
        setSelectedDeleteId(id);
        setIsDeleteDialogVisible(true);
    };

    const handleCloseDeleteDialog = () => {
        if(isDeleting) {
            return;
        }
        setSelectedDeleteId(null);
        setIsDeleteDialogVisible(false);
    };

    const handleDeleteNotification = async () => {
        if(!selectedDeleteId) {
            return;
        }

        try {
            setIsDeleting(true);
            const response = await RESTServiceContext.action("deleteSentNotification", {
                notificationID: selectedDeleteId
            });

            if(!response?.isError && (response?.code === 200 || response?.code === 201 || !response?.code)) {
                NCoreUIKitSnackBar.open({
                    title: localize("notifications-success-delete")
                });
                setIsDeleteDialogVisible(false);
                setSelectedDeleteId(null);
                fetchNotifications(page, debouncedSearchQuery);
            } else {
                NCoreUIKitSnackBar.open({
                    title: response?.message || localize("notifications-error-delete")
                });
            }
        } catch(error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("notifications-error-delete")
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    marginBottom: spaces.spacingXl,
                    gap: spaces.spacingLg
                }
            ]}
        >
            <View
                style={stylesheet.searchContainer}
            >
                <TextInput
                    placeholder={localize("notifications-send-title-placeholder")}
                    onChangeText={(val) => {
                        setSearchQuery(val);
                        setPage(1);
                    }}
                    value={searchQuery}
                />
            </View>
            <Button
                title={localize("notifications-new-notification")}
                customTextColor={colors.project.brandWhite}
                icon={({
                    size
                }) => <PlusIcon
                    color={colors.project.brandWhite}
                    size={size}
                />}
                customBorderColor="secondaryButton"
                customColor="secondaryButton"
                onPress={onOpenSendModal}
                spreadBehaviour="free"
                variant="outline"
            />
        </View>;
    };

    const renderTable = () => {
        if(loading) {
            return <View
                style={[
                    stylesheet.loadingContainer,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.content.border.subtle,
                        borderWidth: borders.seperator,
                        padding: spaces.spacingXl * 2,
                        borderRadius: radiuses.md
                    }
                ]}
            >
                <ActivityIndicator
                    color={colors.content.text.high}
                    size="large"
                />
            </View>;
        }

        if(notifications.length === 0) {
            return <View
                style={[
                    stylesheet.emptyStateContainer,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.content.border.subtle,
                        borderWidth: borders.seperator,
                        padding: spaces.spacingXl * 2,
                        borderRadius: radiuses.md
                    }
                ]}
            >
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {localize("notifications-empty")}
                </Text>
            </View>;
        }

        return <View
            style={[
                stylesheet.tableContainer,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.content.border.subtle,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.md
                }
            ]}
        >
            <View
                style={[
                    stylesheet.tableHeaderRow,
                    {
                        backgroundColor: colors.content.container.primary,
                        borderBottomColor: colors.content.border.subtle,
                        borderBottomWidth: borders.seperator,
                        paddingHorizontal: spaces.spacingLg,
                        paddingVertical: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.titleCell,
                        {
                            paddingRight: spaces.spacingLg
                        }
                    ]}
                >
                    <Text
                        customColor={colors.content.text.mid}
                        variant="bodySmallSize"
                    >
                        {localize("notifications-table-title-content")}
                    </Text>
                </View>
                <View
                    style={[
                        stylesheet.targetCell,
                        {
                            paddingRight: spaces.spacingMd
                        }
                    ]}
                >
                    <Text
                        customColor={colors.content.text.mid}
                        variant="bodySmallSize"
                    >
                        {localize("notifications-table-target")}
                    </Text>
                </View>
                <View
                    style={[
                        stylesheet.statsCell,
                        {
                            paddingRight: spaces.spacingMd
                        }
                    ]}
                >
                    <Text
                        customColor={colors.content.text.mid}
                        variant="bodySmallSize"
                    >
                        {localize("notifications-table-read-rate")}
                    </Text>
                </View>
                <View
                    style={[
                        stylesheet.dateCell,
                        {
                            paddingRight: spaces.spacingMd
                        }
                    ]}
                >
                    <Text
                        customColor={colors.content.text.mid}
                        variant="bodySmallSize"
                    >
                        {localize("notifications-table-date")}
                    </Text>
                </View>
                <View
                    style={stylesheet.actionCell}
                >
                    <Text
                        customColor={colors.content.text.mid}
                        variant="bodySmallSize"
                    >
                        {localize("notifications-table-action")}
                    </Text>
                </View>
            </View>
            {notifications.map((item) => {
                const readRate = Math.min(100, Math.max(0, item.readRate || 0));

                return <View
                    style={[
                        stylesheet.tableDataRow,
                        {
                            borderBottomColor: colors.content.border.subtle,
                            borderBottomWidth: borders.seperator,
                            paddingHorizontal: spaces.spacingLg,
                            paddingVertical: spaces.spacingMd
                        }
                    ]}
                    key={item._id}
                >
                    <View
                        style={stylesheet.titleCell}
                    >
                        <Text
                            customColor={colors.content.text.high}
                            variant="bodyMediumSize"
                            numberOfLines={1}
                        >
                            {item.title}
                        </Text>
                        <Text
                            customColor={colors.content.text.mid}
                            variant="bodySmallSize"
                            numberOfLines={1}
                        >
                            {item.description}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.targetCell}
                    >
                        <View
                            style={[
                                stylesheet.targetBadge,
                                {
                                    backgroundColor: colors.content.container.primary,
                                    paddingHorizontal: spaces.spacingSm,
                                    paddingVertical: spaces.spacingXs,
                                    borderRadius: radiuses.xs
                                }
                            ]}
                        >
                            <Text
                                customColor={colors.content.text.high}
                                variant="bodySmallSize"
                            >
                                {item.sendToAll ? localize("notifications-table-all-users") : `${item.targetCount} ${localize("notifications-table-person-count")}`}
                            </Text>
                        </View>
                    </View>
                    <View
                        style={stylesheet.statsCell}
                    >
                        <Text
                            customColor={colors.content.text.high}
                            variant="bodySmallSize"
                        >
                            {item.readCount} / {item.targetCount} (%{readRate.toFixed(0)})
                        </Text>
                        <View
                            style={[
                                stylesheet.statBarTrack,
                                {
                                    backgroundColor: colors.content.border.subtle,
                                    borderRadius: radiuses.full,
                                    marginTop: spaces.spacingXs,
                                    height: spaces.spacingXs
                                }
                            ]}
                        >
                            <View
                                style={[
                                    stylesheet.statBarFill,
                                    {
                                        backgroundColor: colors.content.text.high,
                                        borderRadius: radiuses.full,
                                        width: `${readRate}%`
                                    }
                                ]}
                            />
                        </View>
                    </View>
                    <View
                        style={stylesheet.dateCell}
                    >
                        <Text
                            customColor={colors.content.text.mid}
                            variant="bodySmallSize"
                        >
                            {moment(item.createdAt).format("DD.MM.YYYY HH:mm")}
                        </Text>
                    </View>
                    <View
                        style={[
                            stylesheet.actionCell,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <Button
                            onPress={() => handleOpenDetail(item._id)}
                            icon={({
                                color,
                                size
                            }) => <EyeIcon
                                color={colors.content.icon[color]}
                                size={size}
                            />}
                            spreadBehaviour="free"
                            variant="ghost"
                            type="neutral"
                            size="small"
                        />
                        <Button
                            onPress={() => handleOpenDeleteDialog(item._id)}
                            icon={({
                                color,
                                size
                            }) => <Trash2Icon
                                color={colors.content.icon[color]}
                                size={size}
                            />}
                            spreadBehaviour="free"
                            variant="ghost"
                            type="danger"
                            size="small"
                        />
                    </View>
                </View>;
            })}
        </View>;
    };

    const renderDeleteDialog = () => {
        return <Dialog
            primaryButtonProps={{
                icon: ({
                    color,
                    size
                }) => <Trash2Icon
                    color={colors.content.icon[color as never] || color}
                    size={size}
                />,
                title: localize("delete") || "Sil",
                onPress: handleDeleteNotification,
                isLoading: isDeleting,
                type: "danger"
            }}
            title={localize("notifications-delete-confirm-title")}
            secondaryButtonProps={{
                title: localize("cancel") || "Vazgeç",
                onPress: handleCloseDeleteDialog,
                variant: "filled",
                type: "neutral"
            }}
            onOverlayPress={handleCloseDeleteDialog}
            onClosed={handleCloseDeleteDialog}
            isVisible={isDeleteDialogVisible}
            variant="yes-no"
        >
            <View
                style={[
                    stylesheet.deleteDialogContent,
                    {
                        padding: spaces.spacingLg
                    }
                ]}
            >
                <Text
                    customColor={colors.content.text.high}
                    variant="bodyMediumSize"
                >
                    {localize("notifications-delete-confirm-desc")}
                </Text>
            </View>
        </Dialog>;
    };

    return <View
        style={stylesheet.container}
    >
        {renderHeader()}
        {renderTable()}
        {
            totalCount > 15 ?
                <View
                    style={[
                        stylesheet.paginationContainer,
                        {
                            marginTop: spaces.spacingLg
                        }
                    ]}
                >
                    <Pagination
                        onSelect={(item) => {
                            setPage(item.pageNumber);
                        }}
                        totalDataCount={totalCount}
                        currentPage={page}
                        itemPerPage={15}
                    />
                </View>
            :
                null
        }
        <NotificationDetailModal
            notificationId={selectedNotificationId}
            isVisible={isDetailModalVisible}
            onClose={handleCloseDetail}
        />
        {renderDeleteDialog()}
    </View>;
};
export default NotificationsList;
